import { addDays } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useAppointments } from "@/features/appointments/hooks/appointmentQueries";
import { businessDayBounds } from "@/features/appointments/utils/bookingTime";
import { useCalendarEvents } from "@/features/calendar-events/hooks/calendarEventQueries";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { RowSkeleton } from "@/shared/components/ui/RowSkeleton";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { calendarDate, calendarDateKey } from "@/shared/utils/calendarDate";
import { formatBusinessDate, getBusinessDateKey } from "@/shared/utils/formatters";
import { DashboardAgendaDay } from "../components/DashboardAgendaDay";
import { DashboardHeader } from "../components/DashboardHeader";
import { useDashboardContext } from "../hooks/useDashboardContext";

const AGENDA_DAYS = 14;

export function AgendaPage() {
  const { user, businessId, locationId, business, location, now } = useDashboardContext();
  const appointments = useAppointments();
  const navigate = useNavigate();
  const { goToAppointmentDetails, goToAppointmentAdd, goToCalendarEventDetails } = useAppNavigation();
  const timeZone = business?.timezone ?? "UTC";
  const today = getBusinessDateKey(now, timeZone);
  const days = Array.from({ length: AGENDA_DAYS }, (_, offset) => calendarDateKey(addDays(calendarDate(today), offset)));
  const events = useCalendarEvents(businessDayBounds(days[0], timeZone).startAt, businessDayBounds(days[days.length - 1], timeZone).endAt);

  if (!user || !business || !location || location.businessId !== businessId) {
    return (
      <Page>
        <DashboardHeader view="agenda" />
        <PageContent>
          <h1 className="sr-only">Agenda</h1>
          {!user ? <RowSkeleton /> : <EmptyState message="This business location is unavailable." />}
        </PageContent>
      </Page>
    );
  }

  const active = (appointments.data || []).filter((appointment) =>
    appointment.businessId === businessId && appointment.locationId === locationId && appointment.startAt
    && !["cancelled", "declined", "no_show"].includes(appointment.status));

  return (
    <Page>
      <DashboardHeader view="agenda" />
      <PageContent className="space-y-[26px] pt-3">
        <h1 className="sr-only">Agenda</h1>
        {events.error && <ErrorState message={`Calendar events unavailable: ${events.error.message}`} onRetry={() => void events.refetch()} />}
        {appointments.error ? <ErrorState message={`Appointments unavailable: ${appointments.error.message}`} onRetry={() => void appointments.refetch()} />
          : appointments.isLoading || events.isLoading ? <RowSkeleton />
          : days.map((dateKey, offset) => (
            <DashboardAgendaDay
              key={dateKey}
              dateKey={dateKey}
              offset={offset}
              timeZone={timeZone}
              appointments={active.filter((appointment) => getBusinessDateKey(appointment.startAt!, timeZone) === dateKey)}
              events={events.data ?? []}
              onViewDay={() => navigate(`/b/${businessId}/l/${locationId}/appointments?date=${dateKey}`)}
              onAppointmentClick={(selected) => goToAppointmentDetails(selected.id, false)}
              onEventClick={(event) => goToCalendarEventDetails(event.id)}
              onGapAdd={(startAt) => goToAppointmentAdd(undefined, { date: getBusinessDateKey(startAt, timeZone), time: formatBusinessDate(startAt, timeZone, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }), startAt: startAt.toISOString() })}
            />
          ))}
      </PageContent>
    </Page>
  );
}
